import type { DropdownEntry } from "~/common/types";
import type { useMyAccountDropdownValues } from "../hooks/useMyAccountDropdownValues";
import type { AffiliatePartnerData } from "~/domains/affiliate/types/models";
import type { TFunction } from "i18next";
import type { RegisterOptions } from "react-hook-form";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEXP,
  ZIPCODE_MAX_LENGTH,
  ZIPCODE_MIN_LENGTH,
  ZIPCODE_REGEXP,
} from "~/common/utils/validations";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";

interface PrefilledCustomerData {
  firstName: string;
  lastName: string;
  companyName: string;
  salutationCode: string;

  line1: string;
  line2: string;
  zipCode: string;
  city: string;
  country: string;
  phone: string;
}

export interface OnboardingFormData {
  titleData: { code: string; name: string }[];
  salutationData: { code: string; name: string }[];
  industrySectorData: { code: string; name: string }[];
  jobTitleData: { code: string; name: string }[];
  functionalAreaData: { code: string; name: string }[];
  countryData: { isocode: string; name: string }[];
  customerCategoryData: { code: string; name: string }[];
  knowledgeAreaData: { code: string; name: string }[];
  languagesData: { isocode: string; name: string }[];
  email: string;
  passwordToken: string;
  validLink: boolean;
  token: string;
  salesPartner?: AffiliatePartnerData;
  customer?: PrefilledCustomerData;
  newsletterOne: boolean;
  selectedCountry?: string;
  zipCode?: string;
}

export interface CompleteRegistrationFormValues {
  title: string;
  salutation: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  company: string;
  industrySector: string;
  jobTitle: string;
  functionalArea: string;
  address: string;
  addressAdditional: string;
  zipCode: string;
  city: string;
  country: string;
  phone: string;
  language: string;
  knowledgeArea: string;
  customerCategory: string;
  vat: string;
  termsTraining: boolean;
}

export interface OnBoradingResponseData {
  data: OnboardingFormData;
}

export async function fetchOnboardingInitialData(token: string): Promise<OnBoradingResponseData> {
  const params = new URLSearchParams();
  params.set("token", token);
  const url = `/page-data/registered/user/complete?${params}`;
  const apiUrl = occUrl(url);
  const response = await occFetch(apiUrl);

  if (!response.ok) {
    throw new Error(`Error status: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

export type Field = {
  name: keyof CompleteRegistrationFormValues;
  label: string;
  type: "text" | "password" | "select";
  dropdownOptions?: DropdownEntry<string>[];
  rules?: RegisterOptions<CompleteRegistrationFormValues>;
};

export type FieldRow = Field | Field[];

export interface SignupStep {
  label: string;
  title: string;
  subtitle: string;
  fields: FieldRow[];
  skipable: boolean;
}

export const createOnboardingSteps = (
  dropdownOptions: ReturnType<typeof useMyAccountDropdownValues>,
  t: TFunction,
): SignupStep[] => {
  const pleaseEnterError = (fieldName: string) => t("page.profile.pleaseEnter." + fieldName);

  const passwordRule = {
    required: pleaseEnterError("password"),
    minLength: {
      value: PASSWORD_MIN_LENGTH,
      message: t("page.completeRegistration.errorMatchRequirements"),
    },
    maxLength: {
      value: PASSWORD_MAX_LENGTH,
      message: t("page.completeRegistration.errorMatchRequirements"),
    },
    pattern: {
      value: PASSWORD_REGEXP,
      message: t("page.completeRegistration.errorMatchRequirements"),
    },
  };

  const confirmPasswordRule: RegisterOptions<CompleteRegistrationFormValues> = {
    required: pleaseEnterError("password"),
    validate: (value, formValues) =>
      value === formValues.password || t("page.completeRegistration.errorTwoPasswordsMatch"),
  };

  const zipCodeRule = {
    minLength: {
      value: ZIPCODE_MIN_LENGTH,
      message: t("checkoutPage.addressModal.error.isTooShortPart", {
        fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
      }),
    },
    maxLength: {
      value: ZIPCODE_MAX_LENGTH,
      message: t("checkoutPage.addressModal.error.isTooLongPart", {
        fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
      }),
    },
    pattern: {
      value: ZIPCODE_REGEXP,
      message: t("checkoutPage.addressModal.error.forbidenCharactersPart", {
        fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
      }),
    },
  };

  return [
    {
      label: t("onboarding.overlay.personalInformation.label"),
      title: t("onboarding.overlay.personalInformation.title"),
      subtitle: t("onboarding.overlay.personalInformation.subtitle"),
      skipable: false,
      fields: [
        {
          name: "salutation",
          label: t("onboarding.overlay.field.salutation"),
          type: "select",
          dropdownOptions: dropdownOptions.salutationOptions,
        },
        {
          name: "firstName",
          label: t("onboarding.overlay.field.firstName"),
          type: "text",
          rules: {
            required: pleaseEnterError("firstName"),
          },
        },
        {
          name: "lastName",
          label: t("onboarding.overlay.field.lastName"),
          type: "text",
          rules: {
            required: pleaseEnterError("lastName"),
          },
        },
      ],
    },
    {
      label: t("onboarding.overlay.password.label"),
      title: t("onboarding.overlay.password.title"),
      subtitle: t("onboarding.overlay.password.subtitle"),
      skipable: false,
      fields: [
        {
          name: "password",
          label: t("onboarding.overlay.field.password"),
          type: "password",
          rules: passwordRule,
        },
        {
          name: "confirmPassword",
          label: t("onboarding.overlay.field.confirmPassword"),
          type: "password",
          rules: confirmPasswordRule,
        },
      ],
    },
    {
      label: t("onboarding.overlay.additionalDetails.label"),
      title: t("onboarding.overlay.additionalDetails.title"),
      subtitle: t("onboarding.overlay.additionalDetails.subtitle"),
      skipable: false,
      fields: [
        {
          name: "company",
          label: t("onboarding.overlay.field.company"),
          type: "text",
          rules: {
            required: pleaseEnterError("companyName"),
          },
        },
        {
          name: "industrySector",
          label: t("onboarding.overlay.field.industrySector"),
          type: "select",
          dropdownOptions: dropdownOptions.industryOptions,
          rules: {
            required: pleaseEnterError("industrySector"),
          },
        },
        {
          name: "country",
          label: t("onboarding.overlay.field.country"),
          type: "select",
          dropdownOptions: dropdownOptions.countryOptions,
          rules: {
            required: pleaseEnterError("country"),
          },
        },
      ],
    },
    {
      label: t("onboarding.overlay.address.label"),
      title: t("onboarding.overlay.address.title"),
      subtitle: t("onboarding.overlay.address.subtitle"),
      skipable: true,
      fields: [
        {
          name: "jobTitle",
          label: t("onboarding.overlay.field.jobTitle"),
          type: "select",
          dropdownOptions: dropdownOptions.jobTitleOptions,
        },
        {
          name: "address",
          label: t("onboarding.overlay.field.address"),
          type: "text",
        },
        {
          name: "addressAdditional",
          label: t("onboarding.overlay.field.addressAdditional"),
          type: "text",
        },
        [
          {
            name: "zipCode",
            label: t("onboarding.overlay.field.zipCode"),
            type: "text",
            rules: zipCodeRule,
          },
          {
            name: "city",
            label: t("onboarding.overlay.field.city"),
            type: "text",
          },
        ],
      ],
    },
    {
      label: t("onboarding.overlay.trainingCampus.label"),
      title: t("onboarding.overlay.trainingCampus.title"),
      subtitle: t("onboarding.overlay.trainingCampus.subtitle"),
      skipable: false,
      fields: [
        {
          name: "language",
          label: t("onboarding.overlay.field.language"),
          type: "select",
          dropdownOptions: dropdownOptions.languageOptions,
          rules: {
            required: pleaseEnterError("language"),
          },
        },
        {
          name: "customerCategory",
          label: t("onboarding.overlay.field.customerCategory"),
          type: "select",
          dropdownOptions: dropdownOptions.customerCategoryOptions,
          rules: {
            required: pleaseEnterError("customerCategory"),
          },
        },
        {
          name: "knowledgeArea",
          label: t("onboarding.overlay.field.knowledgeArea"),
          type: "select",
          dropdownOptions: dropdownOptions.knowledgeAreaOptions,
          rules: {
            required: pleaseEnterError("knowledgeArea"),
          },
        },
      ],
    },
  ];
};
